import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Hotspot from '@wirewaxorg/ww-hotspots';
import { getBrowserVisibilityValues } from '../../../utils/domEvents';
import utils from '../../../utils';
import zIndexes from '../../../styles/zIndexes';

type Props = {
    spriteId: number,
    play: Function,
    pause: Function,
    skip: Function,
    hotspotIsClicked: Function,
    hotspotClickout: Function,
    toggleOverlay: Function,
    closeOverlayAndAnimateOut: Function,
};

const sharableLinks = ['facebook.com/sharer/sharer.php', 'twitter.com/intent/tweet'];

const getIndex = (currentFrame: number, hide: number, show: number) =>
    utils.isVisible(currentFrame, show, hide) ? Math.round(currentFrame - show) : 0;

let spriteIdParamExecuted = false;

const { visibilityChange, hidden } = getBrowserVisibilityValues();
let timeoutId;

/**
 * HotspotAdaptor
 * - get config from both hotspots and interactions entries to create event handler
 * - compose configs for hotspot component (repo)
 */
const HotspotAdaptor = (props: Props) => {
    const {
        spriteId,
        play,
        pause,
        skip,
        hotspotIsClicked,
        hotspotClickout,
        toggleOverlay,
        closeOverlayAndAnimateOut,
    } = props;

    const spriteConnection = useSelector(state => state.hotspots.spriteConnections[spriteId]);

    const {
        coordinates,
        isStatic,
        nameLink,
        pauseOnClick,
        positionX,
        positionY,
        tagText,
        autoClose,
        autoCloseTime,
        inFrame,
        outFrame,
        inSeconds,
        outSeconds,
    } = spriteConnection;

    const interactions = useSelector(state => state.options.activeVideo.interactions);
    const overlays = useSelector(state => state.options.activeVideo.overlays);
    const triggerSpriteId = useSelector(state => state.options.playerParams.triggerSpriteId);
    const videoHotspotDesign = useSelector(state => state.options.activeVideo.hotspotDesign);
    const currentFrame = useSelector(state => state.video.currentFrame);
    const currentTime = useSelector(state => state.video.currentTime);
    const playing = useSelector(state => state.video.playing);
    const isImage = useSelector(state => state.options.isImage);
    const id = useSelector(state => state.options.id);
    const videoTagId = useSelector(state => state.options.activeVideo.video.tagId);
    const customPlayer = useSelector(state => state.options.tpPlayer);
    const overlayClosed = useSelector(
        state => Object.keys(state.options.activeOverlay).length === 0
    );

    const { hotspotDesign, interaction, name } = interactions[nameLink];

    // Handle Overlay auto close
    // Clear timeout on overlay close
    useEffect(
        () => {
            if (overlayClosed) {
                clearTimeout(timeoutId);
            }
        },
        [overlayClosed]
    );

    // Set up time out and clear timeout on switch tab (visibility)
    const setupAutoCloseTimeout = () => {
        const visibilityHandler = () => {
            const isHidden = document[hidden];

            if (!isHidden) return;

            clearTimeout(timeoutId);
        };

        timeoutId = setTimeout(() => {
            closeOverlayAndAnimateOut();
            playing && play();
            document.removeEventListener(visibilityChange, visibilityHandler, false);

        }, autoCloseTime);

        document.addEventListener(visibilityChange, visibilityHandler, false);
    };

    // Handler
    const handleHotspotClick = () => {
        // Metrics
        hotspotIsClicked(nameLink, spriteId);

        if (!interaction) {
            return null;
        }

        const { settings, clickAction } = interaction;

        switch (clickAction) {
            case 'none':
                if (pauseOnClick) {
                    pause();
                }
                break;
            case 'overlay': {
                const overlay = overlays.find(item => item.overlayId === settings.overlayId);

                if (overlay) {
                    overlay.hotspotData = {
                        nameLink,
                        spriteId,
                    };

                    if (pauseOnClick) {
                        pause();
                    }
                    toggleOverlay(overlay);
                }

                if (autoClose && autoCloseTime) {
                    // timeoutId = setTimeout(() => {
                    //     console.log("time is up")
                    //     closeOverlayAndAnimateOut();
                    //     playing && play();
                    // }, autoCloseTime);

                    setupAutoCloseTimeout();
                }

                break;
            }
            case 'url': {
                const query = settings.url.split('?');
                const appendChar = query.length > 1 ? '&' : '?';
                const url = settings.hasUtmString
                    ? `${settings.url}${appendChar}utm_source=wirewax_${id}`
                    : settings.url;
                const isSocialSharingUrl = sharableLinks.find(sharable => url.includes(sharable));

                if (isSocialSharingUrl) {
                    // Open sharable url in a pop up window
                    window.open(url, '', 'width=626,height=436');
                } else {
                    // Open regular url based on config
                    window.open(url, settings.openInNewTab ? '_blank' : '_self');
                }

                hotspotClickout(nameLink, spriteId, url);
                if (pauseOnClick) {
                    pause();
                }
                break;
            }

            case 'seek':
                skip(settings.frame);

                if (pauseOnClick) {
                    pause();
                } else {
                    play();
                }
                break;

            case 'jump':
                skip(settings.jumpFrames + currentFrame);

                // FIXME: take out pauseOnClick checking for now, make it has no impact on playback
                // if (pauseOnClick) {
                //     pause();
                // } else {
                //     play();
                // }

                break;

            default:
                return null;
        }
    };

    // Handle url params ?spriteId=xxxxxxx, the behavior here is re-mount, not re-render
    useEffect(() => {
        // NO spriteId url param or it's already executed
        if (!triggerSpriteId || spriteIdParamExecuted) return;

        // This sprite doesn't match spriteId url param
        if (Number(triggerSpriteId) !== spriteId) return;

        // Seek to the inPoint of matched sprite, and execute its clickAction
        skip(inSeconds, 'secs');
        handleHotspotClick();

        spriteIdParamExecuted = true;
    }, []);

    // Compose props for hotspot repo
    let config = null;
    let presetId = null;

    if (hotspotDesign) {
        ({ config, presetId } = hotspotDesign);
    } else if (videoHotspotDesign) {
        ({ config, presetId } = videoHotspotDesign);
    }

    const index = isStatic ? 0 : getIndex(currentFrame, outFrame, inFrame);

    // FIXME: not sure what error this this is handling
    if (!isImage && !coordinates[index]) {
        return null;
    }

    const position = {
        height: coordinates[index].h,
        left: coordinates[index].x - coordinates[index].w / 2,
        top: coordinates[index].y - coordinates[index].h / 2,
        width: coordinates[index].w,
        zIndex: zIndexes.hotspot,
    };

    return (
        <Hotspot
            config={config}
            containerStyles={position}
            currentSecond={currentTime}
            inSeconds={inSeconds}
            key={spriteId}
            name={name}
            onClick={handleHotspotClick}
            outSeconds={outSeconds}
            playing={playing}
            tag={{ id: presetId || videoTagId }}
            tagText={tagText}
            iconPosition={{ left: positionX, top: positionY }}
            customPlayer={customPlayer}
        />
    );
};

export default HotspotAdaptor;
